import {using} from '@startlibs/utils'
import {jwtPostFetcher} from '../utils/authFetch'
import _ from 'lodash/fp'
import {getJwt} from '../hooks/useJwt'
import {DIGITAL, OFFLINE} from '../enums/PaymentMethod'
import {
  CASE_ARCHIVED, CASE_CLOSED,
  CASE_DRAFT, CASE_REJECTED, CASE_REVIEWED, PENDING_ASSIGNMENT,
  PENDING_REQUEST,
  REQUEST_REJECTED, UNDER_REVIEW, WAITING_ACCEPTANCE,
  WAITING_APPROVAL,
  WAITING_MEDICAL_RECORDS, WAITING_MORE_INFORMATION
} from '../enums/CaseState'
import {isStrEmpty} from '../utils'
import { getUploaderJwt } from '../hooks/useUploaderJwt'

export const isPaymentPending = payment => !payment.paid && !payment.revoked

export const isAlreadyAccepted = (values) => _.get('acceptanceInfo.externallyAccepted',values)||_.get('acceptanceInfo.accepted',values)
export const isAlreadyPaid = (payment) => payment.paid

export const isDigitalPaymentPending = (payment) => isPaymentPending(payment) && payment.paymentMethod === DIGITAL
export const isOfflinePaymentPending = (payment) => isPaymentPending(payment) && payment.paymentMethod === OFFLINE

const STATE_LIST = [CASE_ARCHIVED, REQUEST_REJECTED, PENDING_REQUEST, CASE_DRAFT, WAITING_MEDICAL_RECORDS, WAITING_APPROVAL, PENDING_ASSIGNMENT, CASE_REJECTED, WAITING_ACCEPTANCE, UNDER_REVIEW, WAITING_MORE_INFORMATION, CASE_REVIEWED, CASE_CLOSED]

export const isStateBefore = _.curry((state,of) => STATE_LIST.indexOf(state) < STATE_LIST.indexOf(of))
export const getAdminCaseRequestUrl = (caseRequest,path) => `/admin/request/${caseRequest.requestCode}/${path}`


export const fixMedicareInsideInsurances = _.update('patientInfo.insurances',(insurances) => insurances?.length > 0 ? insurances : [])

// Local-only toggle to fake DELETE /medicalRecords responses instead of hitting the network. Set VITE_MOCK_DELETE_RESPONSE=true in .env
const MOCK_DELETE_RESPONSE = process.env.NODE_ENV === 'development' && false

// NOT_FOUND is intentionally excluded here: it's treated as a silent success (already deleted), not a failure to test.
const MOCK_FAILURE_REASONS = [
  {reason: 'STORAGE_ERROR', message: 'Failed to delete file from storage.'},
  {reason: 'EVE_ERROR', message: 'Failed to remove eve content.'},
  {reason: 'WORKLIST_API_ERROR', message: 'Failed to remove record from worklist.'},
]

const randomFailure = (recordId) => ({recordId, ..._.sample(MOCK_FAILURE_REASONS)})

// Randomly picks one of the 4 scenarios the user needs to be able to test on localhost.
const buildMockDeleteResponse = (recordIds) => {
  const scenario = _.sample(['all_succeed', 'all_fail', 'one_succeeds', 'partial'])
  let response
  if (scenario === 'all_succeed' || recordIds.length === 1) {
    response = {succeeded: recordIds.map((recordId) => ({recordId})), failed: []}
  } else if (scenario === 'all_fail') {
    response = {succeeded: [], failed: recordIds.map(randomFailure)}
  } else if (scenario === 'one_succeeds') {
    const [first, ...rest] = recordIds
    response = {succeeded: [{recordId: first}], failed: rest.map(randomFailure)}
  } else {
    const mid = Math.max(1, Math.floor(recordIds.length / 2))
    response = {
      succeeded: recordIds.slice(0, mid).map((recordId) => ({recordId})),
      failed: recordIds.slice(mid).map(randomFailure)
    }
  }
  console.log(`[MOCK] deleteMedicalRecords scenario="${scenario}"`, response)
  return Promise.resolve(response)
}

// Single bulk call to ExpertView's admin-only delete endpoint; orchestrates storage/Eve cleanup server-side.
export const deleteMedicalRecords = (requestId, recordIds) => {
  if (MOCK_DELETE_RESPONSE) {
    return buildMockDeleteResponse(recordIds)
  }
  if(!requestId ){
    return jwtPostFetcher(getJwt())('/api/medicalRecords',{recordIds},{method:"DELETE"})
  }else{
    return jwtPostFetcher(getJwt())('/api/medicalRecords?requestId='+requestId,{recordIds},{method:"DELETE"})
  }
}

// Local-only toggle to fake DELETE /admin/case responses. Set VITE_MOCK_DELETE_CASE_RESPONSE=true in .env

const MOCK_CASE_WARNINGS = [
  {phase: 'EVE',         target: '8f2cabcdef012345',                      reason: 'EVE_ERROR',           message: 'Eve service unavailable'},
  {phase: 'STORAGE',     target: '4711',                                   reason: 'STORAGE_ERROR',       message: 'File could not be removed'},
  {phase: 'PACS',        target: '1.2.840.113619.2.55.3.604688288.4',      reason: 'WORKLIST_API_ERROR',  message: 'worklist-api unreachable'},
  {phase: 'MPI',         target: 'EV-MOCK',                                reason: 'MPI_ERROR',           message: 'mpi returned 503'},
  {phase: 'LOCAL_FILES', target: '123-customreport-1712345678.pdf',        reason: 'LOCAL_FILE_ERROR',    message: 'unlink failed'},
]

// Cycles all_warnings → some_warnings → no_warnings on successive calls so every UI path is reachable.
let _mockDeleteCaseScenarioIndex = 0
const _MOCK_CASE_SCENARIOS = ['all_warnings', 'some_warnings', 'no_warnings']

const buildMockDeleteCaseResponse = () => {
  const scenario = _MOCK_CASE_SCENARIOS[_mockDeleteCaseScenarioIndex++ % _MOCK_CASE_SCENARIOS.length]
  const warnings = scenario === 'no_warnings'
    ? []
    : scenario === 'some_warnings'
      ? MOCK_CASE_WARNINGS.slice(0, 2)
      : MOCK_CASE_WARNINGS
  const response = {deleted: true, caseId: 0, requestCode: 'EV-MOCK', recordsDeleted: 3, warnings}
  console.log(`[MOCK] deleteCase scenario="${scenario}"`, response)
  return Promise.resolve(response)
}

export const deleteCase = (requestId, reason) => {
  // if (MOCK_DELETE_CASE_RESPONSE) {
  //   return buildMockDeleteCaseResponse()
  // }
  return jwtPostFetcher(getJwt())(`/api/admin/case?requestId=${requestId}`, {reason}, {method: 'DELETE'})
}

const DELETE_FAILURE_REASON_LABELS = {
  STORAGE_ERROR: 'Failed to delete file from storage.',
  EVE_ERROR: 'Failed to remove EVE content.',
  WORKLIST_API_ERROR: 'Failed to remove record from worklist.',
}

// Turns a MedicalRecordsDeleteResponse into: ids that are safe to drop from local state, and the raw per-record
// failures (recordId + message) so the caller can render them (e.g. via the persistent Errors/setError UI).
// NOT_FOUND failures are treated as already-deleted (handled, not an error the user needs to act on).
export const summarizeMedicalRecordsDeleteResponse = (records, response) => {
  const succeeded = response?.succeeded || []
  const failed = response?.failed || []
  const notFound = failed.filter(({reason}) => reason === 'NOT_FOUND')
  const actualFailures = failed.filter(({reason}) => reason !== 'NOT_FOUND')

  const removedIds = succeeded.concat(notFound).map(({recordId}) => String(recordId))
  const failures = actualFailures.map(({recordId, reason}) => ({
    recordId,
    // message: message || DELETE_FAILURE_REASON_LABELS[reason] || reason
    message: DELETE_FAILURE_REASON_LABELS[reason] || reason
  }))

  return {
    removedIds,
    failures,
    notification: (!failures.length && removedIds.length)
      ? (removedIds.length > 1
        ? {type: "success", msg: `${removedIds.length} records successfully deleted.`}
        : {type: "success", msg: "Record successfully deleted."})
      : null
  }
}

export const updateMedicalRecordsInfo = (_postFetcher) => (medicalRecordsStore, setMedicalRecords, requestId) => ({
  add: (object,updater) => {

    return jwtPostFetcher(getJwt())('/api/medicalRecord?requestId='+requestId,_.unset('dicomStudy.instances',object),{method:"POST"})
      .then(() => {
        medicalRecordsStore.set(updater)
        setMedicalRecords(medicalRecordsStore.get())
      })
  },
  remove: (type, uid, updater) => {
    return jwtPostFetcher(getJwt())('/api/medicalRecord?requestId='+requestId,{deleteRequestType:type,mediaRecordID:uid},{method:"DELETE"})
      .then(() => {
        medicalRecordsStore.set(updater)
        setMedicalRecords(medicalRecordsStore.get())
      })
  }
})

export const persistMedicalRecord = (medicalRecordsUpdater,updateGroups) => {
  let lastUpdateGroups = Promise.resolve()
  let isUpdatingGroups = false
  let nextUpdateGroups = null

  const syncUpdateGroup = () => {
    if (isUpdatingGroups) {
      return lastUpdateGroups
    } else {
      const next = () => {
        isUpdatingGroups = true
        const currentGroups = nextUpdateGroups
        return updateGroups(nextUpdateGroups)
          .catch(e => {
            isUpdatingGroups = false
            return Promise.reject(e)
          })
          .then(() => {
            isUpdatingGroups = false
            if (currentGroups !== nextUpdateGroups) {
              syncUpdateGroup()
            }
          })

      }
      lastUpdateGroups = next()
      return lastUpdateGroups
    }
  }

  return ({dicomStudy, nonCompliant, nonDicomFile, groups}) => {
    if (groups) {
      nextUpdateGroups = groups
      return syncUpdateGroup()
    }
    if (dicomStudy) {
      return medicalRecordsUpdater.add({dicomStudy},_.update(
        'dicomStudies',
        (studies) => {
          const studyIndex = studies.findIndex(({studyUID}) => studyUID === dicomStudy.studyUID)
          if (studyIndex >= 0) {
            return _.set([studyIndex], _.omit(['instances', 'isNew'], dicomStudy), studies)
          } else {
            return studies.concat(_.omit(['instances', 'isNew'], dicomStudy))
          }
        }
      ))
    } else if (nonDicomFile) {
      return medicalRecordsUpdater.add({nonDicomFile},_.update(
        'nonDicomFiles',
        _.unionBy(_.property('uid'), [nonDicomFile])
      ))
    } else if (nonCompliant) {
      return medicalRecordsUpdater.add({nonCompliantDicom:nonCompliant},
        _.update(
          ['nonCompliantDicom'],
          _.unionBy(_.property('uid'), [nonCompliant])
        )
      )
    }
    return Promise.resolve()
  }
}


export const hasAnsweredAll = (list) => list?.length && !list?.find(({answer}) => isStrEmpty(answer))
export const hasAFieldsFilled = (caseInfo) => using(
  (caseInfo.additionalFields||[]).map(({title,content})=> ({label:title,value:content})).concat((caseInfo.questionsConsultant||[]).map(({question,answer})=> ({label:question,value:answer})))
)(fields =>
  fields?.length && fields.find(({label,value})=> !isStrEmpty(label) && !isStrEmpty(value))
)

const isHttpUrl = (url) => {
  try {
    return ['http:', 'https:'].includes(new URL(url, window.location.href).protocol)
  } catch {
    return false
  }
}

export const startDownload = (launchURL) => {
  if (!isHttpUrl(launchURL)) return
  const link = document.createElement('a')
  link.href = launchURL
  link.style.visibility = "hidden"
  link.setAttribute("download", "")
  document.body.appendChild(link);
  link.click()
  link.remove()
}

export const downloadFiles = (jwt, selectedRecords) => {
  // const downloaderURL = process.env.NODE_ENV === 'development' ? "https://downloader.vivacloud.io/records?recordId=" : window.DOWNLOADER_URL || "https://downloader.purview.net/v2/records?recordId=";
  const downloaderURL = process.env.NODE_ENV === 'development' ? "https://dev-downloader-us-east-2.purview.net/records?recordId=" : window.DOWNLOADER_URL || "https://downloader.purview.net/v2/records?recordId=";
  const downloadPromise =
  selectedRecords.length
    ? startDownload(downloaderURL+selectedRecords.map((item) => item).join(",")+'&jwt='+jwt)
    : Promise.resolve(false)
  return downloadPromise
 
}

export const viewFileSrc = (jwt, recordId ) => {
  const downloaderURL = process.env.NODE_ENV === 'development' ? "https://dev-downloader-us-east-2.purview.net/records?recordId=" : window.DOWNLOADER_URL || "https://downloader.purview.net/v2/records?recordId=";
  const viewURL = downloaderURL+recordId+'&jwt='+jwt+'&attachment=false'
  
  return viewURL
}


export const pdfIframeSrc = (record) => {

  // const downloaderUrl = process.env.NODE_ENV === 'development' ? "https://downloader.vivacloud.io/records?recordId=" : window.DOWNLOADER_URL || "https://downloader.purview.net/v2/records?recordId=";
  const downloaderUrl = process.env.NODE_ENV === 'development' ? "https://dev-downloader-us-east-2.purview.net/records?recordId=" : window.DOWNLOADER_URL || "https://downloader.purview.net/v2/records?recordId=";
  const pdfSrc = downloaderUrl+record+'&jwt='+getUploaderJwt()+'&attachment=false'

  return pdfSrc
}